import type {
  ZodError,
  ZodFirstPartySchemaTypes,
  ZodFirstPartyTypeKind,
  ZodIssue,
} from 'zod';

import { CustomSchemaTypes, SchemaTypes } from './schema-types';

const diveIntoZodDef = <T>(prop: ZodMergedType & ZodExtendType<T>) => {
  let types = [prop._def.typeName];
  let checks = (prop._def.checks || []) as Array<Record<string, unknown>>;
  let shape = prop.shape || null;
  let arrayType = prop._def.type || null;
  let recordType = prop._def.valueType || null;
  let unionTypes = prop._def.typeName === 'ZodUnion' ? prop._def.options : null;

  if (prop._def.minLength) {
    checks.push({
      kind: 'min',
      ...prop._def.minLength,
    });
  }
  if (prop._def.maxLength) {
    checks.push({
      kind: 'max',
      ...prop._def.maxLength,
    });
  }

  if (prop._customData?.fileTypes) {
    checks.push({
      kind: 'fileTypes',
      value: prop._customData.fileTypes,
    });
  }

  if (checks.some(check => check.kind === 'email')) {
    types.push(CustomSchemaTypes.SchemaEmail as ZodFirstPartyTypeKind);
  }

  if (prop._customType !== undefined) {
    types.push(prop._customType as ZodFirstPartyTypeKind);
  }

  const { innerType, schema } = prop._def;
  [innerType, schema].forEach(defProp => {
    if (defProp) {
      const {
        types: innerTypes,
        checks: innerChecks,
        shape: innerShape,
        arrayType: innerArrayType,
        recordType: innerRecordType,
        unionTypes: innerUnionTypes,
      } = diveIntoZodDef(defProp);
      types = [...types, ...innerTypes];
      checks = [...checks, ...innerChecks];
      if (innerShape) {
        shape = innerShape;
      }

      if (innerArrayType) {
        arrayType = innerArrayType;
      }

      if (innerRecordType) {
        recordType = innerRecordType;
      }

      if (innerUnionTypes) {
        unionTypes = innerUnionTypes;
      }
    }
  });

  return { types, checks, shape, arrayType, recordType, unionTypes };
};
const formatProperty = <T>(
  prop: ZodMergedType & ZodExtendType<T>
): SchemaDefinitionProperty => {
  const { types, checks, shape, arrayType, recordType, unionTypes } =
    diveIntoZodDef(prop);

  const searchCustomTypes = Object.keys(CustomSchemaTypes).find(type =>
    types.includes(type as ZodFirstPartyTypeKind)
  ) as keyof typeof CustomSchemaTypes | undefined;

  const searchCommonTypes = Object.keys(SchemaTypes).find(type =>
    types.includes(type as ZodFirstPartyTypeKind)
  ) as keyof typeof SchemaTypes | undefined;

  const foundCustomType =
    !!searchCustomTypes &&
    searchCustomTypes in CustomSchemaTypes &&
    CustomSchemaTypes[searchCustomTypes];
  const foundSchemaType =
    !!searchCommonTypes &&
    searchCommonTypes in SchemaTypes &&
    SchemaTypes[searchCommonTypes];

  const schemaType = foundCustomType || foundSchemaType;
  if (!schemaType) {
    throw new Error(
      `Cannot find known schema prop: ${JSON.stringify(types, null, 2)}`
    );
  }

  const formattedProp = {
    type: schemaType,
    constraints: {
      optional: types.some(
        type => type === 'ZodOptional' || type === 'ZodNullable'
      ),
      maxLength: checks.find(check => check.kind === 'max')?.value,
      minLength: checks.find(check => check.kind === 'min')?.value,
      fileTypes: checks.find(check => check.kind === 'fileTypes')?.value,
    },
    children: null,
  } as unknown as SchemaDefinitionProperty;

  if (shape) {
    formattedProp.children = Object.keys(shape).reduce(
      (acc: SchemaDefinitionProperty['children'], propName: string) => {
        const fProp = formatProperty<T>(shape[propName]);

        if (acc) {
          acc[propName] = fProp;
        }

        return acc;
      },
      {}
    );
  }

  if (arrayType) {
    formattedProp.arrayType = formatProperty<T>(arrayType);
  }

  if (recordType) {
    formattedProp.recordType = formatProperty<T>(recordType);
  }

  if (unionTypes) {
    formattedProp.unionTypes = unionTypes.map(
      (type: ZodFirstPartySchemaTypes & ZodExtendType<T>) =>
        formatProperty<T>(type)
    );
  }

  return formattedProp;
};

const setStructure = (
  structure: Record<string, unknown> | unknown[] | undefined,
  keys: Array<string | number>,
  value: unknown
): unknown => {
  if (keys.length === 0) {
    return value;
  }

  const key = keys.shift();

  if (Number.isInteger(key)) {
    const arrKey = key as number;
    if (!Array.isArray(structure)) {
      structure = [];
    }
    structure[arrKey] = setStructure(
      structure[arrKey] as number[],
      keys,
      value
    );
  } else {
    if (!structure) {
      structure = {} as Record<string, unknown>;
    }

    const strutKey = key as string;
    let innerValue = (structure as Record<string, unknown>)[strutKey];
    innerValue = setStructure(
      innerValue as Record<string, unknown>,
      keys,
      value
    );
    (structure as Record<string, unknown>)[strutKey] = innerValue;
  }

  return structure;
};

const formatErrors = (errors?: ZodIssue[]) => {
  return errors?.reduce((acc: unknown, err) => {
    if (err.code === 'invalid_union' && err.unionErrors[0]?.issues?.[0]) {
      [err] = err.unionErrors[0].issues;
    }

    let errorMessage = err.message;

    if (
      err.code === 'invalid_type' &&
      err.received === 'null' &&
      err.message.startsWith('Expected')
    ) {
      errorMessage = 'Required';
    }

    if (
      err.code === 'invalid_string' &&
      err.validation === 'uuid' &&
      err.message === 'Invalid uuid'
    ) {
      errorMessage = 'Required';
    }

    if (err.code === 'custom' && err.message === 'Input not instance of File') {
      errorMessage = 'Required';
    }
    if (
      err.code === 'too_big' &&
      err.type === 'string' &&
      err.message.startsWith('String')
    ) {
      errorMessage = `Maximum character limit reached: ${err.maximum}`;
    }

    if (err.path.length > 0) {
      setStructure(acc as Record<string, string>, err.path, errorMessage);
      return acc;
    }
    return errorMessage || acc;
  }, {});
};

export const SchemaTypesUtil = {
  format: (schema: ZodMergedType): SchemaDefinition => {
    return formatProperty(schema as ZodMergedType & ZodExtendType<unknown>);
  },
  validate: (schema: ZodMergedType, formData: unknown) => {
    const parsedStructure = schema.safeParse(formData) as {
      success: boolean;
      data?: unknown;
      error?: ZodError;
    };
    const returnStructure = {
      errors: formatErrors(parsedStructure.error?.issues) || null,
    } as { errors?: Record<string, unknown>; values?: unknown };
    if (parsedStructure.data !== undefined) {
      returnStructure.values = parsedStructure.data;
    }
    return returnStructure;
  },
};
